/**
 * NotepadPane -- yellow post-it style scratch surface.
 *
 * Free-form markdown-flavoured notes the candidate can jot down while
 * talking. Mirrors CodingPane's contract:
 *
 *   - onChange({ content, char_count }) on every edit; parent owns
 *     debouncing via useArtifactSync.
 *   - onEvent('clear' | 'submit') for discrete intents (parent relays
 *     them straight to the AI as "[Editor event] candidate just did:
 *     notepad.clear" so the model can react to a fresh-page signal).
 *
 * UX is deliberately quiet: a yellow post-it surface, soft shadow, no
 * borders, just text. The "AI sees your notes" pulse mirrors the other
 * panels so the candidate knows the model is reading along (the
 * post-it nature is *informational* -- the AI uses it as light context,
 * never reacts directly to keystrokes).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Trash2, Send, FileText } from 'lucide-react';

interface NotepadPaneProps {
  lang: 'de' | 'en';
  onChange: (payload: { content: string; char_count: number }) => void;
  onEvent: (action: 'submit' | 'clear', details?: Record<string, unknown>) => void;
  disabled?: boolean;
  /** Epoch ms of the last snapshot pushed to the server. Drives the
   *  brief "AI sees your notes" pulse. */
  lastPushedAt?: number | null;
  pendingEdit?: boolean;
}

const MAX_CHARS = 12000; // matches the server-side cap in interview_ws.py

const NotepadPane: React.FC<NotepadPaneProps> = ({
  lang,
  onChange,
  onEvent,
  disabled,
  lastPushedAt,
  pendingEdit,
}) => {
  const [content, setContent] = useState<string>('');
  const [justPushed, setJustPushed] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!lastPushedAt) return;
    setJustPushed(true);
    const t = setTimeout(() => setJustPushed(false), 1500);
    return () => clearTimeout(t);
  }, [lastPushedAt]);

  // Autofocus on mount so the candidate can start typing immediately
  // when they switch to the notepad tab. Don't steal focus away if a
  // re-render fires for unrelated reasons.
  useEffect(() => {
    if (disabled) return;
    const ta = textareaRef.current;
    if (!ta) return;
    if (document.activeElement === ta) return;
    // Only autofocus on first mount, not every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      const v = value.length > MAX_CHARS ? value.slice(0, MAX_CHARS) : value;
      setContent(v);
      onChange({ content: v, char_count: v.length });
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    setContent('');
    onChange({ content: '', char_count: 0 });
    onEvent('clear');
  }, [onChange, onEvent]);

  const handleSubmit = useCallback(() => {
    onEvent('submit', { char_count: content.length });
  }, [content.length, onEvent]);

  const t = useMemo(
    () => ({
      title:        lang === 'de' ? 'Notizblock' : 'Notepad',
      hint:
        lang === 'de'
          ? 'Stichpunkte, Storyline, Annahmen, Zahlen ... für dich.'
          : 'Bullet points, storyline, assumptions, numbers ... for you.',
      clear:        lang === 'de' ? 'Leeren' : 'Clear',
      submit:       lang === 'de' ? 'Markieren' : 'Mark',
      submitTitle:
        lang === 'de'
          ? 'Sag der KI, dass dieser Stand für dich abgeschlossen ist'
          : 'Signal the AI that this snapshot is complete from your side',
      placeholder:
        lang === 'de'
          ? 'Notizen für dich. Die KI sieht den aktuellen Text mit, reagiert aber nicht auf jeden Tipp - sie nutzt ihn als leichten Kontext.\n\n- Annahmen:\n- Story (S-T-A-R):\n- Hypothesen:\n- Trade-offs:'
          : 'Your private scratchpad. The AI reads the current text but won\'t react to every keystroke - it uses it as light context.\n\n- Assumptions:\n- Story (S-T-A-R):\n- Hypotheses:\n- Trade-offs:',
      aiSeesPushed:  lang === 'de' ? 'AI sieht deine Notizen' : 'AI sees your notes',
      aiSeesPending: lang === 'de' ? 'Wird an die AI gesendet ...' : 'Syncing to AI ...',
      aiSeesIdle:    lang === 'de' ? 'Schreib - die AI liest mit' : 'Write - the AI reads along',
      chars:         (n: number) => lang === 'de' ? `${n.toLocaleString('de-DE')} Zeichen` : `${n} characters`,
    }),
    [lang],
  );

  const awarenessLabel = pendingEdit
    ? t.aiSeesPending
    : lastPushedAt
      ? t.aiSeesPushed
      : t.aiSeesIdle;

  return (
    <div className="flex flex-col h-full rounded-2xl overflow-hidden bg-white border border-white/10 isolate">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-yellow-200/80 bg-yellow-50/90 gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-yellow-800/80 flex-shrink-0">
            <FileText className="w-3.5 h-3.5" />
            {t.title}
          </span>
          <span
            className={[
              'inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border whitespace-nowrap transition-colors flex-shrink-0',
              justPushed
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : pendingEdit
                  ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-white border-yellow-200 text-yellow-800/70',
            ].join(' ')}
            title={awarenessLabel}
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className={[
                  'absolute inline-flex h-full w-full rounded-full opacity-75',
                  justPushed
                    ? 'bg-emerald-500 animate-ping'
                    : pendingEdit
                      ? 'bg-amber-500 animate-pulse'
                      : 'bg-yellow-400',
                ].join(' ')}
              />
              <span
                className={[
                  'relative inline-flex h-1.5 w-1.5 rounded-full',
                  justPushed ? 'bg-emerald-500' : pendingEdit ? 'bg-amber-500' : 'bg-yellow-500',
                ].join(' ')}
              />
            </span>
            <Eye className="w-3 h-3" />
            <span className="hidden sm:inline">{awarenessLabel}</span>
          </span>
          <span className="text-[11px] text-yellow-800/60 truncate hidden md:inline">
            {t.hint}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[10px] font-medium text-yellow-800/60 tabular-nums hidden sm:inline">
            {t.chars(content.length)}
          </span>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled || content.length === 0}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-yellow-900/70 bg-white/70 border border-yellow-200 hover:bg-white disabled:opacity-40"
          >
            <Trash2 className="w-3 h-3" />
            {t.clear}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || content.trim().length === 0}
            title={t.submitTitle}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold text-white bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 disabled:opacity-40"
          >
            <Send className="w-3 h-3" />
            {t.submit}
          </button>
        </div>
      </div>

      {/* Post-it surface. Light yellow paper, ruled background lines for
          a familiar legal-pad feel without being too busy. */}
      <div
        className="flex-1 min-h-0 relative bg-[#fffbe6]"
        style={{
          backgroundImage:
            'linear-gradient(transparent calc(1.5rem - 1px), rgba(217, 119, 6, 0.12) calc(1.5rem - 1px), rgba(217, 119, 6, 0.12) 1.5rem, transparent 1.5rem)',
          backgroundSize: '100% 1.5rem',
          backgroundPosition: '0 1.05rem',
        }}
      >
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => handleChange(e.target.value)}
          disabled={disabled}
          placeholder={t.placeholder}
          spellCheck
          className="w-full h-full resize-none bg-transparent outline-none px-6 py-4 text-[14px] leading-6 text-amber-950 placeholder:text-amber-700/40 font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace] selection:bg-yellow-300/60"
          maxLength={MAX_CHARS}
        />
        {/* Tiny corner fold to remind it's a post-it (decorative only) */}
        <div
          aria-hidden
          className="absolute top-0 right-0 w-6 h-6 pointer-events-none"
          style={{
            background:
              'linear-gradient(225deg, transparent 50%, rgba(217, 119, 6, 0.18) 50%)',
          }}
        />
      </div>
    </div>
  );
};

export default NotepadPane;
